import React from 'react';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import TopicIcon from './TopicIcon';
import TopicNameWithIcon from './TopicNameWithIcon';
import SelectEntityOption from '../components/SelectEntityOption';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { TopicMatchKey } from '../search/entitySearch/localEntitySearch';
import LocalSearchHighlight from '../search/LocalSearchHighlight';
import type { FuzzySearchMatch } from '../utils/search/fuzzySearch';

const TOPIC_FRAGMENT = gql(`
  fragment TopicSelectOption_topic on Topic {
    id
    name
    ancestors {
      name
      ...TopicBreadcrumbs_topics
    }
    ...TopicNameWithIcon_topic
  }
`);

interface TopicSelectOptionProps {
  name?: React.ReactNode;
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  matches?: readonly FuzzySearchMatch[];
  hideIcon?: boolean;
  focused?: boolean;
  truncate?: boolean;
  disabled?: boolean;
  secondary?: React.ReactNode;
  secondaryTop?: React.ReactNode;
}

const TopicSelectOption = React.forwardRef<
  HTMLDivElement,
  TopicSelectOptionProps
>(
  (
    {
      name,
      topic,
      matches,
      hideIcon,
      truncate,
      disabled,
      secondaryTop,
      ...props
    }: TopicSelectOptionProps,
    ref,
  ) => (
    <SelectEntityOption
      {...props}
      icon={!hideIcon && <TopicIcon />}
      disabled={disabled}
      secondaryTop={
        secondaryTop ??
        (topic.ancestors && topic.ancestors.length > 0 && (
          <TopicBreadcrumbs
            names={topic.ancestors.map((ancestor, index) => (
              <LocalSearchHighlight
                key={ancestor.id}
                matchKey={TopicMatchKey.AncestorNames}
                refIndex={index}
                matches={matches}
                defaultValue={ancestor.name}
              />
            ))}
            topics={topic.ancestors}
            size="small"
            truncate={truncate}
          />
        ))
      }
      label={
        <TopicNameWithIcon
          name={
            <LocalSearchHighlight
              matches={matches}
              matchKey={TopicMatchKey.Name}
              defaultValue={name || topic.name}
            />
          }
          topic={topic}
        />
      }
      ref={ref}
    />
  ),
);

export default TopicSelectOption;
